<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-primary" @click="handleCreate">
        <i class="ri-add-line"></i> 新增角色
      </button>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋角色代碼或角色名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchRoles">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>角色代碼</th>
            <th>角色名稱</th>
            <th>適用帳號類型</th>
            <th>說明</th>
            <th>狀態</th>
            <th>使用帳號數</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="roles.length === 0">
            <td colspan="7" class="empty-state">
              沒有找到角色
            </td>
          </tr>
          <tr v-for="role in roles" :key="role.role_code">
            <td><code>{{ role.role_code }}</code></td>
            <td>{{ role.role_name }}</td>
            <td>
              <span :class="getAccountTypeBadgeClass(role.account_type)">
                {{ getAccountTypeText(role.account_type) }}
              </span>
            </td>
            <td>{{ role.description || '-' }}</td>
            <td>
              <span :class="role.status === 'active' ? 'badge badge-success' : 'badge badge-secondary'">
                {{ role.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ role.account_count || 0 }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary"
                  @click="handleEdit(role)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(role)"
                  :title="role.account_count > 0 ? '此角色正在使用中，無法刪除' : '刪除'"
                  :disabled="role.account_count > 0"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { rolesService } from '@/api/modules/roles.js';
import { showError, showConfirm } from '@/utils/swal.js';

export default {
  name: 'RolesList',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('角色管理');
    const roles = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchRoles = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: Number(currentPage.value),
          pageSize: Number(pageSize.value),
        };

        if (searchQuery.value && searchQuery.value.trim().length > 0) {
          params.search = searchQuery.value.trim();
        }

        const response = await rolesService.getRoles(params);

        if (response && response.success && response.data) {
          roles.value = response.data.items || [];
          total.value = response.data.total || 0;
        } else {
          error.value = response && response.message || '獲取角色列表失敗';
        }
      } catch (err) {
        console.error('獲取角色列表失敗:', err);
        error.value = err.message || '獲取角色列表失敗';
        await showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchRoles();
      }, 500);
    };

    const handleFilter = () => {
      currentPage.value = 1;
      fetchRoles();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchRoles();
      }
    };

    const handleCreate = () => {
      router.push('/system/roles/create');
    };

    const handleEdit = (role) => {
      router.push(`/system/roles/${role.role_code}/edit`);
    };

    const handleManageModules = (role) => {
      router.push(`/system/roles/${role.role_code}/modules`);
    };

    const handleDelete = async (role) => {
      // 檢查是否有帳號正在使用此角色
      if (role.account_count > 0) {
        await showError(
          '無法刪除',
          `角色「${role.role_name}」目前有 ${role.account_count} 個帳號正在使用，無法刪除。`
        );
        return;
      }

      const confirmed = await showConfirm(
        '確認刪除',
        `確定要刪除角色「${role.role_name}」嗎？此操作無法復原。`,
        '刪除',
        '取消',
        'warning'
      );

      if (confirmed) {
        try {
          await rolesService.deleteRole(role.role_code);
          await fetchRoles();
        } catch (err) {
          console.error('刪除角色失敗:', err);
          await showError('錯誤', err.response?.data?.message || '刪除角色失敗');
        }
      }
    };

    const getAccountTypeText = (accountType) => {
      const map = {
        system: '系統帳號',
        platform: '平台帳號',
        client: '客戶帳號',
      };
      return map[accountType] || accountType;
    };

    const getAccountTypeBadgeClass = (accountType) => {
      const map = {
        system: 'badge badge-primary',
        platform: 'badge badge-info',
        client: 'badge badge-success',
      };
      return map[accountType] || 'badge badge-secondary';
    };

    onMounted(() => {
      fetchRoles();
    });

    return {
      roles,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      total,
      pageTitle,
      handleSearch,
      handleFilter,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      fetchRoles,
      getAccountTypeText,
      getAccountTypeBadgeClass
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
/* 頁面特定樣式（如有需要可在此添加） */
</style>
